import { ApolloLink } from '@apollo/client/core';
import Observable from 'zen-observable';
import { prepareApolloAuthRetry } from './apollo-auth-retry';

const isUnauthorized = (error) => error?.statusCode === 401;

export function createApolloAuthRecoveryLink({ auth, telemetry }) {
  let signInPending = false;
  let rollbackPromise;

  const beginSignInOnce = () => {
    if (signInPending) return;
    signInPending = true;
    Promise.resolve()
      .then(() => auth.beginSignIn())
      .catch(() => {
        signInPending = false;
      });
  };

  const redirectForRollback = () => {
    if (rollbackPromise) return rollbackPromise;
    rollbackPromise = Promise.resolve()
      .then(() => auth.getUser())
      .then((user) => auth.signoutRedirect({ id_token_hint: user?.id_token || '' }))
      .catch((error) => {
        rollbackPromise = undefined;
        throw error;
      });
    return rollbackPromise;
  };

  return new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        let disposed = false;
        let primary;
        let retry;
        const replay = (renewalEnabled) => {
          if (disposed) return;
          let recorded = false;
          retry = forward(operation).subscribe({
            next: (result) => {
              if (disposed) return;
              if (renewalEnabled && !recorded && !result.errors?.length) {
                recorded = true;
                telemetry.recordAuthEvent('auth_request_retry', {
                  outcome: 'success',
                  reason: 'http_401',
                  durationMs: 0,
                });
              }
              observer.next(result);
            },
            error: (error) => {
              if (disposed) return;
              const retryUnauthorized = isUnauthorized(error);
              observer.error(error);
              if (retryUnauthorized && renewalEnabled) beginSignInOnce();
            },
            complete: () => observer.complete(),
          });
        };

        primary = forward(operation).subscribe({
          next: (result) => observer.next(result),
          error: (networkError) => {
            if (disposed) return;
            if (!isUnauthorized(networkError)) {
              observer.error(networkError);
              return;
            }
            if (auth.isAuthSessionRenewalEnabled) {
              prepareApolloAuthRetry(operation, auth.refreshSession)
                .then((shouldRetry) => {
                  if (disposed) return;
                  if (shouldRetry) replay(true);
                  else {
                    observer.error(networkError);
                    beginSignInOnce();
                  }
                })
                .catch((error) => {
                  if (disposed) return;
                  observer.error(error);
                  beginSignInOnce();
                });
            } else {
              redirectForRollback().then(
                () => replay(false),
                (error) => {
                  if (!disposed) observer.error(error);
                },
              );
            }
          },
          complete: () => observer.complete(),
        });

        return () => {
          disposed = true;
          primary?.unsubscribe();
          retry?.unsubscribe();
        };
      }),
  );
}
